import React, { useEffect, useState } from 'react';
import {MembershipUtils} from "../model/MembershipUtils";
import { GAUtils } from "../model/GAUtils";

interface CreditNotification {
  id: number;
  message: string;
  type: 'consumed' | 'cached';
}

const SearchResults: React.FC = () => {
  const [notifications, setNotifications] = useState<CreditNotification[]>([]);

  const showNotification = (message: string, type: 'consumed' | 'cached') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    
    // Auto-remove after 3 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  };

  useEffect(() => {
    // 定义回调函数
    const getGcsKeywordFromHash = () => {
      const hash = window.location.hash; // "#gsc.tab=0&gsc.q=keyword&gsc.sort="

      if (!hash.includes("gsc.q=")) return ''

      const params = new URLSearchParams(hash.substring(1)); // Remove the "#" symbol
      const keyword = params.get("gsc.q");

      return keyword ? decodeURIComponent(keyword) : '';
    }

    const myWebResultsRenderedCallback = async () => {
      const keyword = getGcsKeywordFromHash();
      
      // Check if this is a cached query before consuming
      const cachedQueries = JSON.parse(localStorage.getItem('searched_queries') || '[]');
      const normalizedKeyword = keyword.toLowerCase().trim();
      const isCached = cachedQueries.includes(normalizedKeyword);
      
      const success = await MembershipUtils.recordAccess(keyword);
      
      // Count results for analytics
      const resultElements = document.querySelectorAll('.gsc-webResult .gs-title');
      const resultCount = resultElements.length;
      
      // Track search keyword for keyword analysis
      if (keyword) {
        GAUtils.trackSearchKeyword(keyword, resultCount, isCached);
        GAUtils.trackSearchResultsView(keyword, resultCount);
      }
      
      if (success && keyword) {
        // Track credit consumption
        const creditsRemaining = parseInt(localStorage.getItem('user_credits') || '0', 10);
        GAUtils.trackCreditConsumed(keyword, creditsRemaining, isCached);
        
        if (isCached) {
          showNotification(`"${keyword}" - Free (cached)`, 'cached');
        } else {
          showNotification(`-1 credit used for "${keyword}"`, 'consumed');
        }
      }
      
      const links = document.querySelectorAll('.gsc-webResult .gs-title');
      links.forEach((link, index) => {
        if (link instanceof HTMLAnchorElement) {
          if (link.getAttribute('data-customized')) return;

          const originalHref = link.getAttribute('href'); // 获取原始链接
          if (originalHref) {
            // Mark as customized to avoid duplicate event listeners
            link.setAttribute('data-customized', 'true');
            // 防止默认行为并重定向
            link.addEventListener('click', (e) => {
              e.preventDefault();
              // Track search result click
              GAUtils.trackSearchResultClick(keyword, originalHref, index + 1);
              window.open(originalHref, '_blank'); // 新窗口打开
            });
          }
        }
      });
    };

    // 配置全局回调
    (window as any).__gcse = (window as any).__gcse || {};
    (window as any).__gcse.searchCallbacks = {
      web: {
        rendered: myWebResultsRenderedCallback,
      },
    };

    return () => {
      // 如果需要清理逻辑，可以在这里进行
    };
  }, []);

  return (
    <div id="search-results-container">
      {/* Credit notification toast */}
      <div style={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        {notifications.map((notification) => (
          <div
            key={notification.id}
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              background: notification.type === 'consumed' 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              color: 'white',
              fontSize: '14px',
              fontWeight: 500,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              animation: 'slideIn 0.3s ease-out',
              maxWidth: '300px',
            }}
          >
            <span style={{ fontSize: '16px' }}>
              {notification.type === 'consumed' ? '💰' : '✨'}
            </span>
            <span style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap' 
            }}>
              {notification.message}
            </span>
          </div>
        ))}
      </div>
      
      {/* Keyframe animation */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default SearchResults;
